/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import { InfoCircleFilled } from "@ant-design/icons";
import { Pie } from "@ant-design/plots";
import { DatePicker, Descriptions, Select, Switch } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { useGraphTheme } from "../../../../hooks/useGraphTheme";
import { selectFormOptionsNoFormatting } from "../../../../utils/helper";
import { showErrorNotification, showWarningNotification } from "../../../../utils/notification";
import { clamp } from "../../../../utils/responsiveHelpers";
import AnalyticsGenericBarGraph from "../../../common-utils/GenericBarGraph";
import ShowTransferredQueryDetailsModal from "../../helper/ShowTransferredQueryDetailsModal";
import ShowUnsatisfiedQueryDetailsModal from "../../helper/ShowUnsatisfiedQueryDetailsModal";
import { unsatisfiedReasonsPieConfig } from "../../helper/ShowUnsatisfiedQueryDetailsModal/graphConfigs";

const DiscordAnalyticsMentorPerformanceDash = ({ ROOT_URL }) => {
  const graphTheme = useGraphTheme();
  const BASE_BACKEND_URL = "admin/discord-analytics/mentors/performance-analytics";
  const currentDate = new Date();
  const twoMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 2,
    currentDate.getDate()
  );

  const [mentorType, setMentorType] = useState("");
  const [openQueriesCount, setOpenQueriesCount] = useState("");
  const [totalQueriesCount, setTotalQueriesCount] = useState(0);
  const [totalThankPoints, setTotalThankPoints] = useState(0);
  const [totalThankCount, setTotalThankCount] = useState(0);
  const [totalUnsatisfiedQueriesCount, setTotalUnsatisfiedQueriesCount] = useState(0);
  const [totalTransfers, setTotalTransfers] = useState(0);
  const [levelWiseQueryCountDataPoints, setLevelWiseQueryCountDataPoints] = useState([]);
  const [levelWiseThankCountBarGraphPoints, setLevelWiseThankCountBarGraphPoints] = useState([]);
  const [dateWiseUnsatisfiedQueryBarGraphPoints, setDateWiseUnsatisfiedQueryBarGraphPoints] =
    useState([]);
  const [dateWiseAllLevelTotalThanksBarGraphPoints, setDateWiseAllLevelTotalThanksBarGraphPoints] =
    useState([]);
  const [batches, setBatches] = useState([]);
  const [mentorNameByBatches, setMentorNamesByBatches] = useState({});
  const [mentorNamesList, setMentorNamesList] = useState([]);
  const [filter, setFilter] = useState({
    startDate: twoMonthAgo,
    endDate: currentDate
  });
  const [unSatisfiedQueriesList, setUnSatisfiedQueriesList] = useState();
  const [isUnsatisfiedQueryListModalOpen, setIsUnsatisfiedQueryListModalOpen] = useState(false);
  const [unsatisfiedReasonsData, setUnsatisfiedReasonData] = useState([]);
  const [transferredQueriesList, setTransferredQueriesList] = useState();
  const [isTransferredQueryListModalOpen, setIsTransferredQueryListModalOpen] = useState(false);
  const [isMentorActive, setIsMentorActive] = useState(true);

  const pieConfig = {
    data: levelWiseQueryCountDataPoints || [],
    angleField: "value",
    colorField: "type",
    innerRadius: 0.5,
    label: {
      text: "value",
      style: {
        fontWeight: "bold"
      }
    },
    legend: {
      color: {
        title: false,
        position: "right",
        rowPadding: 5
      }
    },
    annotations: [
      {
        type: "text",
        style: {
          text: "Level Distribution",
          x: "50%",
          y: "50%",
          textAlign: "center",
          fontSize: 20,
          fontStyle: "bold"
        }
      }
    ]
  };

  const fetchMentorNamesList = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/mentor-names`, {
        params: { isMentorActive: isMentorActive }
      })
      .then((res) => {
        const { mentorNamesByBatches } = res.data;
        setBatches(Object.keys(mentorNamesByBatches));
        setMentorNamesByBatches(mentorNamesByBatches);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchMentorMetadata = () => {
    if (!filter.mentorDiscordId) {
      return;
    }
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/data`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const {
          mentorType,
          openQueries,
          totalQueries,
          totalThankPoints,
          aggregatedTotalThanks,
          unsatisfiedQueriesCount,
          levelWiseQueryCountPiePoints,
          levelWiseThankCountBarGPoints,
          unsatisfiedQueriesCountBarGPoints,
          allLevelTotalQueriesCountBarGPoints,
          totalTransfers
        } = res.data;
        setMentorType(mentorType);
        setOpenQueriesCount(openQueries);
        setTotalQueriesCount(totalQueries);
        setTotalThankPoints(totalThankPoints);
        setTotalThankCount(aggregatedTotalThanks);
        setTotalUnsatisfiedQueriesCount(unsatisfiedQueriesCount);
        setLevelWiseQueryCountDataPoints(levelWiseQueryCountPiePoints);
        setLevelWiseThankCountBarGraphPoints(levelWiseThankCountBarGPoints);
        setDateWiseUnsatisfiedQueryBarGraphPoints(unsatisfiedQueriesCountBarGPoints);
        setDateWiseAllLevelTotalThanksBarGraphPoints(allLevelTotalQueriesCountBarGPoints);
        setTotalTransfers(totalTransfers);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleBatchFilterChange = (selectedBatch) => {
    setMentorNamesList(mentorNameByBatches[selectedBatch]);
  };

  const fetchUnsatisfiedQueryDetails = () => {
    if (!filter.mentorDiscordId) {
      showWarningNotification("Please select mentor");
      return;
    }
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/unsatisfied-queries`, {
        params: { filter }
      })
      .then((res) => {
        setUnSatisfiedQueriesList(res.data);
        setIsUnsatisfiedQueryListModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchUnsatisfiedReasonStats = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/unsatisfied-queries-reasons-analytics`, {
        params: {
          filter
        }
      })
      .then((res) => {
        setUnsatisfiedReasonData(res.data);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchTransferredQueryDetails = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/transferred-queries`, {
        params: { filter }
      })
      .then((res) => {
        setTransferredQueriesList(res.data);
        setIsTransferredQueryListModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  useEffect(() => {
    fetchMentorNamesList();
  }, [isMentorActive]);

  useEffect(() => {
    fetchMentorMetadata();
    filter.mentorDiscordId && fetchUnsatisfiedReasonStats();
  }, [filter]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "5px"
        }}
      >
        <Select
          showSearch
          placeholder="Select Batch"
          style={{ width: "min(200px, 100%)", marginBottom: 10 }}
          optionFilterProp="children"
          onChange={(value) => {
            handleBatchFilterChange(value);
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {selectFormOptionsNoFormatting(batches || []).map((batch) => (
            <Select.Option value={batch.value} key={batch.value}>
              {batch.label}
            </Select.Option>
          ))}
        </Select>
        <Select
          showSearch
          placeholder="Select Mentors"
          style={{ width: "min(200px, 100%)", marginBottom: 10 }}
          optionFilterProp="children"
          onChange={(value) => {
            setFilter((prevFilter) => ({
              ...prevFilter,
              mentorDiscordId: value
            }));
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {(mentorNamesList || []).map((mentor) => (
            <Select.Option value={mentor.value} key={mentor.value}>
              {mentor.label}
            </Select.Option>
          ))}
        </Select>
        Date Range:{" "}
        <DatePicker.RangePicker
          style={{ width: "50%" }}
          value={
            filter.startDate && filter.endDate
              ? [dayjs(filter.startDate), dayjs(filter.endDate)]
              : []
          }
          onChange={(dates) => {
            if (dates) {
              setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: dates[0].clone().startOf("day").toDate(),
                endDate: dates[1].clone().endOf("day").toDate()
              }));
            } else {
              setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: null,
                endDate: null
              }));
            }
          }}
        />
        <div>
          <Switch checked={isMentorActive} onChange={(checked) => setIsMentorActive(checked)} />{" "}
          Show Only Active Mentors
        </div>
      </div>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <Descriptions title="" bordered column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Type">{mentorType}</Descriptions.Item>
          <Descriptions.Item label="Open Queries">{openQueriesCount}</Descriptions.Item>
          <Descriptions.Item label="Total Queries">{totalQueriesCount}</Descriptions.Item>
          <Descriptions.Item label="Total Thank Count">{totalThankCount}</Descriptions.Item>
          <Descriptions.Item label="Total Thank Points">{totalThankPoints}</Descriptions.Item>
          <Descriptions.Item label="Unsatisfied Queries">
            {totalUnsatisfiedQueriesCount}{" "}
            <InfoCircleFilled
              onClick={() => {
                fetchUnsatisfiedQueryDetails();
              }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Transferred Query">
            {totalTransfers}{" "}
            <InfoCircleFilled
              onClick={() => {
                fetchTransferredQueryDetails();
              }}
            />
          </Descriptions.Item>
        </Descriptions>
      </div>

      <h3>Query Distribution</h3>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <Pie
          key={JSON.stringify(levelWiseQueryCountDataPoints)}
          {...pieConfig}
          {...graphTheme}
          legend={{
            ...pieConfig.legend,
            ...graphTheme.legend
          }}
        />
      </div>

      <h3>Daily Picked Queries</h3>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph dataPoints={dateWiseAllLevelTotalThanksBarGraphPoints} />
      </div>

      <h3>Daily Unsatisfied Queries</h3>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph dataPoints={dateWiseUnsatisfiedQueryBarGraphPoints} />
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Unsatisfied Queries Reasons Stats</h3>
        <Pie
          key={JSON.stringify(unsatisfiedReasonsData)}
          data={unsatisfiedReasonsData || []}
          {...unsatisfiedReasonsPieConfig}
          {...graphTheme}
          legend={{
            ...unsatisfiedReasonsPieConfig.legend,
            ...graphTheme.legend
          }}
        />
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        {(Object.keys(levelWiseThankCountBarGraphPoints) || []).sort().map((level) => {
          const levelThankCountDayWisePoints = levelWiseThankCountBarGraphPoints[level] || [];
          return (
            <>
              <h3>{level} Daily Thank Count</h3>
              <AnalyticsGenericBarGraph dataPoints={levelThankCountDayWisePoints} />
            </>
          );
        })}
      </div>
      {isUnsatisfiedQueryListModalOpen && (
        <ShowUnsatisfiedQueryDetailsModal
          isModalOpen={isUnsatisfiedQueryListModalOpen}
          setIsModalOpen={setIsUnsatisfiedQueryListModalOpen}
          unSatisfiedQueriesList={unSatisfiedQueriesList || []}
        />
      )}
      {isTransferredQueryListModalOpen && (
        <ShowTransferredQueryDetailsModal
          isModalOpen={isTransferredQueryListModalOpen}
          setIsModalOpen={setIsTransferredQueryListModalOpen}
          transferredQueriesList={transferredQueriesList || []}
        />
      )}
    </div>
  );
};

export default DiscordAnalyticsMentorPerformanceDash;
